import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { Icons } from '../shared/icons';
import { TIERS, FAQS } from './content';

const check = (key) => <SafeIcon key={key} icon={Icons.Check} />;

export default function Pricing() {
  const [params, setParams] = useSearchParams();
  const annual = params.get('billing') === 'annual';
  const [openFaq, setOpenFaq] = useState(null);

  const setBilling = (isAnnual) => {
    setParams(isAnnual ? { billing: 'annual' } : {}, { replace: true });
  };

  return (
    <>
      {/* ---------------- hero ---------------- */}
      <section className="mk-hero">
        <div className="mk-container">
          <span className="mk-hero-badge">
            <SafeIcon icon={Icons.Cash} />
            One price, everything included — no per-user fees
          </span>
          <h1>
            Pricing that scales with your agency, <em>not your headcount</em>
          </h1>
          <p className="mk-hero-sub">
            Start at £49/mo. Professional (£129/mo) adds the engineer portal, statutory
            gas certificates and Stripe payments. Business (£299/mo) adds AI and full
            offline sync. Annual billing saves 15–17%.
          </p>
          <div className="mk-hero-ctas">
            <Link to="/sign-up" className="mk-btn mk-btn-primary">
              Book a demo
              <SafeIcon icon={Icons.ArrowRight} />
            </Link>
            <Link to="/features" className="mk-btn mk-btn-outline">
              Compare features
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------- tiers ---------------- */}
      <section className="mk-section">
        <div className="mk-container">
          <div className="mk-toggle" role="group" aria-label="Billing period">
            <button
              type="button"
              className={!annual ? 'mk-on' : ''}
              onClick={() => setBilling(false)}
            >
              Monthly
            </button>
            <button
              type="button"
              className={annual ? 'mk-on' : ''}
              onClick={() => setBilling(true)}
            >
              Annual
            </button>
            <span className="mk-toggle-note">save 15–17%</span>
          </div>

          <div className="mk-tiers" style={{ marginTop: 34 }}>
            {TIERS.map((t) => {
              const price = annual ? t.annual : t.monthly;
              return (
                <div className={`mk-tier${t.featured ? ' mk-tier-featured' : ''}`} key={t.id}>
                  {t.featured && <span className="mk-tier-flag">Most popular</span>}
                  <h3>{t.name}</h3>
                  <p className="mk-tier-target">{t.target}</p>
                  <div className="mk-tier-price">
                    <strong>{price ? `£${price}` : 'Bespoke'}</strong>
                    <span>{price ? (annual ? '/yr' : '/mo') : ''}</span>
                  </div>
                  <p className="mk-tier-bill-note">
                    {price ? t.annualNote : 'from £599/mo — let\u2019s talk'}
                  </p>
                  <ul>
                    {t.features.map((f) => (
                      <li key={f}>{check(f)}{f}</li>
                    ))}
                  </ul>
                  <Link to={`/sign-up?tier=${t.id}`} className="mk-btn mk-btn-primary">
                    {t.cta}
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="mk-pricing-foot">
            <strong>Intro offer:</strong> the first 10 agencies get their first 3 months
            at 50% off. <strong>Payment processing:</strong> Starter has no card
            processing; Professional adds 1.5% + £0.20 over Stripe’s fees, Business 0.8%
            + £0.20 — both undercut Jobber (2.4% + $0.30) and Housecall Pro (2.65% +
            $0.35). All prices exclude VAT.
          </div>
        </div>
      </section>

      {/* ---------------- ROI band ---------------- */}
      <section className="mk-section mk-unique">
        <div className="mk-container">
          <div className="mk-section-head">
            <span className="mk-eyebrow">The maths</span>
            <h2>Professional pays for itself 6–12× over</h2>
            <p>
              Numbers from our commercial review (docs/pricing.md §4) — based on what
              agencies actually spend today on certificate software, chasing payments and
              paper job sheets.
            </p>
          </div>
          <div className="mk-unique-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="mk-unique-item">
              <SafeIcon icon={Icons.Clock} />
              <h3>5–10 hrs/week saved</h3>
              <p>Chasing invoices and updates by phone/email — at £25/hr that’s £500–1,000/mo.</p>
            </div>
            <div className="mk-unique-item">
              <SafeIcon icon={Icons.FileText} />
              <h3>£250–750/yr on certificates</h3>
              <p>Versus manual completion or separate Gas Safe Register software.</p>
            </div>
            <div className="mk-unique-item">
              <SafeIcon icon={Icons.Tool} />
              <h3>3–5 hrs/week per engineer</h3>
              <p>Paper job sheets replaced by digital forms and signatures.</p>
            </div>
            <div className="mk-unique-item">
              <SafeIcon icon={Icons.Cash} />
              <h3>6–12× net ROI</h3>
              <p>Typical agency on Professional vs the old way of working.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section className="mk-section">
        <div className="mk-container">
          <div className="mk-section-head">
            <span className="mk-eyebrow">FAQ</span>
            <h2>Questions agencies ask us</h2>
          </div>
          <div className="mk-faqs">
            {FAQS.map((f, i) => (
              <div className="mk-faq" key={f.q}>
                <button
                  type="button"
                  className="mk-faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span>{f.q}</span>
                  <SafeIcon icon={openFaq === i ? Icons.ChevronDown : Icons.ChevronRight} />
                </button>
                {openFaq === i && <p className="mk-faq-a">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="mk-cta-band">
        <div className="mk-container">
          <h2>Try it free — set up in minutes</h2>
          <p>
            Tell us about your agency and we’ll get your branded portal running with your
            first clients loaded.
          </p>
          <div className="mk-hero-ctas">
            <Link to="/sign-up" className="mk-btn mk-btn-lime">
              Book a demo
              <SafeIcon icon={Icons.ArrowRight} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
